// Public read-only view of an architecture via a share token. No auth.
// Reuses the DiagramCanvas + NodeDetailModal for visual parity with the
// authoring page, but strips ALL editing chrome (no sidebar, no Cmd+K, no
// save button, no right-click context menu, no version history).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";

import DiagramCanvas from "../components/output/DiagramCanvas";
import NodeDetailModal from "../components/output/NodeDetailModal";
import { api, formatApiErrorDetail } from "../lib/api";
import { PROVIDER_LABELS } from "../lib/archModel";

export default function ProjectShare() {
  const { token } = useParams();
  const [arch, setArch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [focusTarget, setFocusTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/share/${token}`)
      .then(({ data }) => {
        if (!cancelled) setArch(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        toast.error("Couldn't load shared architecture");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const toggleCollapse = useCallback((id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const focusNode = useCallback(
    (id) => {
      if (!arch) return;
      const byId = new Map(arch.nodes.map((n) => [n.id, n]));
      const ancestors = [];
      let cur = byId.get(id)?.parentId;
      while (cur) {
        ancestors.push(cur);
        cur = byId.get(cur)?.parentId;
      }
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        ancestors.forEach((a) => next.delete(a));
        return next;
      });
      setSelectedId(id);
      setModalOpen(true);
      setFocusTarget(id);
    },
    [arch]
  );

  const selectedNode = useMemo(
    () => (arch && selectedId ? arch.nodes.find((n) => n.id === selectedId) : null),
    [arch, selectedId]
  );
  const parentOfSelected = useMemo(
    () => (arch && selectedNode?.parentId ? arch.nodes.find((n) => n.id === selectedNode.parentId) : null),
    [arch, selectedNode]
  );
  const childrenOfSelected = useMemo(
    () => (arch && selectedNode ? arch.nodes.filter((n) => n.parentId === selectedNode.id) : []),
    [arch, selectedNode]
  );

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--color-background-primary)" }}
        data-testid="share-loading"
      >
        <p style={{ color: "var(--color-text-tertiary)", fontSize: 13 }}>Loading shared architecture…</p>
      </div>
    );
  }

  if (error || !arch) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "var(--color-background-primary)" }}
        data-testid="share-error"
      >
        <div style={{ textAlign: "center", maxWidth: 460 }}>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: "var(--color-text-primary)",
              marginBottom: 8,
              letterSpacing: "-0.02em",
            }}
          >
            Link not found
          </h1>
          <p style={{ color: "var(--color-text-secondary)", fontSize: 14, marginBottom: 16 }}>
            {error || "This share link is invalid or has been revoked."}
          </p>
          <Link
            to="/"
            className="motion-micro inline-flex items-center gap-1.5"
            style={{
              background: "var(--color-accent)",
              color: "#0a0a0a",
              padding: "9px 16px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            Back to Guided Sentry
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--color-background-primary)", height: "100vh" }}
      data-testid="share-page"
    >
      <header
        className="sticky top-0 z-30 border-b"
        style={{
          background: "rgba(10,10,10,0.92)",
          backdropFilter: "blur(12px)",
          borderColor: "var(--color-border-primary)",
        }}
      >
        <div className="max-w-none mx-auto px-6 h-[64px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/"
              className="motion-micro inline-flex items-center gap-1.5"
              data-testid="share-brand"
              style={{
                textDecoration: "none",
                color: "var(--color-text-primary)",
                fontSize: 14,
                fontWeight: 600,
                fontFamily: "JetBrains Mono, monospace",
                letterSpacing: "-0.01em",
              }}
            >
              <Sparkles size={14} color="var(--color-accent)" /> guided sentry
            </Link>
            <span style={{ width: 1, height: 22, background: "var(--color-border-primary)" }} />
            <span
              data-testid="share-arch-name"
              style={{
                color: "var(--color-text-primary)",
                fontSize: 14,
                fontWeight: 600,
                fontFamily: "JetBrains Mono, monospace",
              }}
            >
              {arch.name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="font-mono"
              style={{
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "4px 10px",
                borderRadius: 9999,
                background: "var(--color-background-secondary)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-secondary)",
              }}
            >
              {PROVIDER_LABELS[arch.provider] || arch.provider}
            </span>
            <span
              className="font-mono"
              data-testid="share-readonly-pill"
              style={{
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "4px 10px",
                borderRadius: 9999,
                background: "color-mix(in oklab, var(--color-accent) 14%, transparent)",
                border: "1px solid color-mix(in oklab, var(--color-accent) 45%, transparent)",
                color: "var(--color-accent)",
                fontWeight: 600,
              }}
            >
              READ&#8209;ONLY
            </span>
            <Link
              to="/"
              data-testid="share-cta-design-your-own"
              className="motion-micro inline-flex items-center gap-1.5"
              style={{
                background: "var(--color-accent)",
                color: "#0a0a0a",
                height: 32,
                padding: "0 14px",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                textDecoration: "none",
                letterSpacing: "-0.005em",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 16px -8px color-mix(in oklab, var(--color-accent) 60%, transparent)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              Design your own <ArrowRight size={13} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 relative" data-testid="share-canvas-container">
          <DiagramCanvas
            nodes={arch.nodes}
            peerConnections={arch.peerConnections}
            collapsedIds={collapsedIds}
            onToggleCollapse={toggleCollapse}
            onNodeClick={(id) => {
              setSelectedId(id);
              setModalOpen(true);
            }}
            onNodeContextMenu={() => {}}
            onDropPayload={() => {}}
            selectedId={selectedId}
            focusTarget={focusTarget}
            onAfterFocus={() => setFocusTarget(null)}
            draggingPayload={null}
            pendingPlacement={null}
            peerConnectMode={null}
            provider={arch.provider}
          />
        </div>
      </div>

      <NodeDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        node={selectedNode}
        parentNode={parentOfSelected}
        childNodes={childrenOfSelected}
        peerConnections={arch.peerConnections}
        allNodes={arch.nodes}
        provider={arch.provider}
        onFocusNode={focusNode}
        // Editing callbacks are intentionally omitted — the modal hides its
        // action toolbar and the peer × buttons when these are falsy.
      />
    </div>
  );
}
